import { useMsal } from "@azure/msal-react";
import {
    Button,
    FormControl,
    ListSubheader,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    Snackbar,
    Alert,
    styled
} from "@mui/material";
import { CircularProgress } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import axios from "axios";
import { baseURL } from "../Constant";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { submitCampaignDesign, resetState } from "../Redux/CampaignDesignSlice";
import "./Design.css";
import WelcomeNameContainer from '../LaunchComponent/WelcomeNameContainer';

const WhiteBorderTextField = styled(TextField)`
 & .MuiOutlinedInput-root {
  &.Mui-focused fieldset {
   border-color: #a814ff;
  }
 }
`;

const CampaignForm = () => {
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();

    let loggedInEnterpriseId = activeAccount?.idTokenClaims?.enterpriseID || "";

    const [campaignName, setCampaignName] = useState("");
    const [selectedMarket, setSelectedMarket] = useState("");
    const [isVerified, setIsVerified] = useState(false);
    const [otherTrack, setOtherTrack] = useState("");

    const [programName, setProgramName] = useState("");
    const [programLead, setProgramLead] = useState("");
    const [programObjective, setProgramObjective] = useState("");
    const [programType, setProgramType] = useState("");
    const [otherProgramType, setOtherProgramType] = useState("");
    const [targetAudience, setTargetAudience] = useState("");
    const [collateralType, setCollateralType] = useState([]);
    const [otherCollateral, setOtherCollateral] = useState("");
    const [noOfCollaterals, setNoOfCollaterals] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [notes, setNotes] = useState("");
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState({
        open: false,
        message: "",
        severity: "success", // success | error
    });
    const { loading, success, error } = useSelector(
        (state) => state.campaignDesign
    );
    const [dragActive, setDragActive] = useState(false);

    const commonStackStyle = {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
    };

    const resetForm = () => {
        setCampaignName("");
        setSelectedMarket("");
        setOtherTrack("");
        setProgramName("");
        setProgramLead("");
        setProgramObjective("");
        setProgramType("");
        setOtherProgramType("");
        setTargetAudience("");
        setCollateralType([]);
        setOtherCollateral("");
        setNoOfCollaterals("");
        setStartDate("");
        setEndDate("");
        setNotes("");
        setFiles([]);
        setIsVerified(false);
    };

    const handleCollateralChange = (type) => {
        if (collateralType.includes(type)) {
            setCollateralType(collateralType.filter(item => item !== type));
        } else {
            setCollateralType([...collateralType, type]);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileChange({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);

        let updatedFiles = [...files, ...newFiles];

        // limit to 10 files
        if (updatedFiles.length > 10) {
            updatedFiles = updatedFiles.slice(0, 10);
            setToast({
                open: true,
                message: "Maximum 10 files allowed",
                severity: "error"
            });
        }

        setFiles(updatedFiles);
    };

    const handleDeleteFile = (index) => {
        const updated = files.filter((_, i) => i !== index);
        setFiles(updated);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        let newErrors = {};

        if (!selectedMarket) newErrors.selectedMarket = "Required";
        if (selectedMarket === "Other" && !otherTrack) newErrors.otherTrack = "Required";

        if (!programName) newErrors.programName = "Required";
        if (!programLead) newErrors.programLead = "Required";
        if (!programObjective) newErrors.programObjective = "Required";

        if (!programType) newErrors.programType = "Required";
        if (programType === "Other" && !otherProgramType) newErrors.otherProgramType = "Required";

        if (!targetAudience) newErrors.targetAudience = "Required";
        if (collateralType.length === 0) {
            newErrors.collateralType = "Required";
        }

        if (collateralType.includes("Other") && !otherCollateral) {
            newErrors.otherCollateral = "Required";
        }
        if (!noOfCollaterals) newErrors.noOfCollaterals = "Required";

        if (!startDate) newErrors.startDate = "Required";
        if (!endDate) newErrors.endDate = "Required";

        if (startDate && endDate && new Date(endDate) <= new Date(startDate)) {
            newErrors.endDate = "End date must be later than start date";
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) return;

        const formData = new FormData();

        const finalCollateral = collateralType.includes("Other")
            ? [...collateralType.filter(t => t !== "Other"), otherCollateral]
            : collateralType;

        formData.append("name", loggedInEnterpriseId);
        formData.append("created_by", loggedInEnterpriseId);
        formData.append("market", selectedMarket === "Other" ? otherTrack : selectedMarket);
        formData.append("program_name", programName);
        formData.append("program_lead", programLead);
        formData.append("program_objective", programObjective);
        formData.append("program_type", programType === "Other" ? otherProgramType : programType);
        formData.append("target_audience", targetAudience);
        formData.append("collateral_type", finalCollateral.join(", "));
        formData.append("no_of_collaterals", noOfCollaterals);
        formData.append("start_date", startDate);
        formData.append("end_date", endDate);
        formData.append("notes", notes);

        files.forEach(file => formData.append("files", file));

        dispatch(submitCampaignDesign(formData));
    };

    const getNextDay = (date) => {
        if (!date) return "";
        const d = new Date(date);
        d.setDate(d.getDate() + 1);
        return d.toISOString().split("T")[0];
    };

    useEffect(() => {
        if (success) {
            setToast({
                open: true,
                message: "Design submitted successfully!",
                severity: "success",
            });

            resetForm();
            dispatch(resetState());
        }

        if (error) {
            setToast({
                open: true,
                message: "Submission failed. Please try again.",
                severity: "error",
            });

            console.error(error);
            dispatch(resetState());
        }
    }, [success, error]);

    useEffect(() => {
        return () => {
            files.forEach(file => URL.revokeObjectURL(file));
        };
    }, [files]);

    return (
        <div>
            <WelcomeNameContainer currentComp="design" />
            <Stack component="form" onSubmit={handleSubmit} className="mail-form-container-stack">
                <div className="mail-form-container">
                    <div className="mail-form-wrap">
                        <Stack sx={commonStackStyle}>
                            <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>Program Name <span style={{ color: "red" }}>*</span></Typography>
                            <WhiteBorderTextField
                                size="small"
                                value={programName}
                                onChange={(e) => setProgramName(e.target.value)}
                                error={!!errors.programName}
                                helperText={errors.programName}
                            />
                        </Stack>

                        <Stack sx={commonStackStyle}>
                            <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>Program Lead <span style={{ color: "red" }}>*</span></Typography>
                            <WhiteBorderTextField
                                size="small"
                                value={programLead}
                                onChange={(e) => setProgramLead(e.target.value)}
                                error={!!errors.programLead}
                                helperText={errors.programLead}
                            />
                        </Stack>

                        <Stack sx={commonStackStyle}>
                            <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>Program Objective <span style={{ color: "red" }}>*</span></Typography>
                            <WhiteBorderTextField
                                size="small"
                                minRows={2}
                                maxRows={3}
                                value={programObjective}
                                onChange={(e) => setProgramObjective(e.target.value)}
                                error={!!errors.programObjective}
                                helperText={errors.programObjective}
                            />
                        </Stack>

                        <Stack sx={commonStackStyle}>
                            <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>No. of Collaterals <span style={{ color: "red" }}>*</span></Typography>
                            <WhiteBorderTextField
                                size="small"
                                type="number"
                                value={noOfCollaterals}
                                onChange={(e) => setNoOfCollaterals(e.target.value)}
                                error={!!errors.noOfCollaterals}
                                helperText={errors.noOfCollaterals}
                            />
                        </Stack>

                        <Stack sx={commonStackStyle}>
                            <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>Program Launch Date <span style={{ color: "red" }}>*</span></Typography>
                            <WhiteBorderTextField
                                size="small"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                error={!!errors.startDate}
                                helperText={errors.startDate}
                            />
                        </Stack>

                        <Stack sx={commonStackStyle}>
                            <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>Program End Date <span style={{ color: "red" }}>*</span></Typography>
                            <WhiteBorderTextField
                                size="small"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                error={!!errors.endDate}
                                helperText={errors.endDate}
                                inputProps={{
                                    min: getNextDay(startDate) || undefined
                                }}
                            />
                        </Stack>
                    </div>
                </div>

                <Stack sx={commonStackStyle}>
                    <Typography sx={{ fontSize: "14px", fontWeight: "bold" }} mt="2rem">Market/Track <span style={{ color: "red" }}>*</span></Typography>

                    <div className="mail-form-wrap">
                        {[
                            "Delivery",
                            "AI",
                            "Cloud",
                            "Foreign Language",
                            "BIHS",
                            "L&TT Ops",
                            "E&IT",
                            "ATCI SONG",
                            "Industry Learning",
                            "LDE nexT",
                            "Learning Experiences (LX)",
                            "ATC GN",
                            "Other"
                        ].map((track) => (
                            <label key={track} className="mailer-form-categories-container">
                                {track}
                                <input
                                    type="radio"
                                    name="marketTrack"
                                    checked={selectedMarket === track}
                                    onChange={() => setSelectedMarket(track)}
                                />
                                <span className="mailer-form-categories-checkmark"></span>
                            </label>
                        ))}
                        {errors.selectedMarket && (
                            <Typography sx={{ color: "red", fontSize: "12px" }}>
                                {errors.selectedMarket}
                            </Typography>
                        )}
                    </div>
                </Stack>

                {selectedMarket === "Other" && (
                    <Stack sx={commonStackStyle}>
                        <WhiteBorderTextField
                            size="small"
                            label="Specify Track"
                            value={otherTrack}
                            onChange={(e) => setOtherTrack(e.target.value)}
                            error={!!errors.otherTrack}
                            helperText={errors.otherTrack}
                            sx={{ marginTop: "1rem" }}
                        />
                    </Stack>
                )}

                <Stack sx={commonStackStyle}>
                    <Typography sx={{ fontSize: "14px", fontWeight: "bold" }} mt="2rem">Program Type <span style={{ color: "red" }}>*</span></Typography>
                    <div className="mail-form-wrap">
                        {["Learning Program", "Certification Program", "Event/Webinar", "Other"].map(type => (
                            <label key={type} className="mailer-form-categories-container">
                                {type}
                                <input type="radio" checked={programType === type} onChange={() => setProgramType(type)} />
                                <span className="mailer-form-categories-checkmark"></span>
                            </label>
                        ))}
                    </div>
                    {errors.programType && (
                        <Typography sx={{ color: "red", fontSize: "12px" }}>
                            {errors.programType}
                        </Typography>
                    )}
                </Stack>

                {programType === "Other" && (
                    <Stack sx={commonStackStyle}>
                        <WhiteBorderTextField
                            size="small"
                            value={otherProgramType}
                            label="Specify Program Type"
                            onChange={(e) => setOtherProgramType(e.target.value)}
                            error={!!errors.otherProgramType}
                            helperText={errors.otherProgramType}
                            sx={{ marginTop: "1rem" }}
                        />
                    </Stack>
                )}

                {/* TARGET */}
                <Stack sx={commonStackStyle}>
                    <Typography sx={{ fontSize: "14px", fontWeight: "bold" }} mt="2rem">Target Audience <span style={{ color: "red" }}>*</span></Typography>
                    <WhiteBorderTextField
                        size="small"
                        value={targetAudience}
                        placeholder="Eg: Oracle professionals; 5k+"
                        onChange={(e) => setTargetAudience(e.target.value)}
                        error={!!errors.targetAudience}
                        helperText={errors.targetAudience}
                    />
                </Stack>

                {/* COLLATERAL */}
                <Stack sx={commonStackStyle}>
                    <Typography sx={{ fontSize: "14px", fontWeight: "bold" }} mt="2rem">
                        Type of Collateral <span style={{ color: "red" }}>*</span>
                    </Typography>

                    <div className="mail-form-wrap">
                        {[
                            "PPT(s)",
                            "Video",
                            "MS Teams Background",
                            "Banners",
                            "Flairs",
                            "Infographics",
                            "Digital Badges",
                            "Other"
                        ].map(type => (
                            <label key={type} className="mailer-form-categories-container">
                                {type}
                                <input
                                    type="checkbox"
                                    checked={collateralType.includes(type)}
                                    onChange={() => handleCollateralChange(type)}
                                />
                                <span className="mailer-form-categories-checkmark"></span>
                            </label>
                        ))}
                    </div>

                    {errors.collateralType && (
                        <Typography sx={{ color: "red", fontSize: "12px" }}>
                            {errors.collateralType}
                        </Typography>
                    )}
                </Stack>

                {collateralType.includes("Other") && (
                    <Stack sx={commonStackStyle}>
                        <WhiteBorderTextField
                            size="small"
                            label="Specify Other Collateral"
                            value={otherCollateral}
                            onChange={(e) => setOtherCollateral(e.target.value)}
                            error={!!errors.otherCollateral}
                            helperText={errors.otherCollateral}
                            sx={{ marginTop: "1rem" }}
                        />
                    </Stack>
                )}

                {/* NOTES */}
                <Stack sx={commonStackStyle}>
                    <Typography sx={{ fontSize: "14px", fontWeight: "bold" }} mt="2rem">Additional Notes</Typography>
                    <WhiteBorderTextField size="small" multiline minRows={2} maxRows={3} onChange={(e) => setNotes(e.target.value)} />
                </Stack>

                {/* FILE */}
                <Typography sx={{ fontSize: "14px", fontWeight: "bold" }} mt="2rem" mb="0.5rem">Upload Files</Typography>
                <div className="additional-wrap">
                    <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        style={{
                            border: dragActive ? "2px dashed #1976d2" : "2px dashed #ccc",
                            padding: "20px",
                            textAlign: "center",
                            borderRadius: "8px",
                            background: dragActive ? "#f0f7ff" : "#fafafa",
                            cursor: "pointer",
                        }}
                    >
                        <Typography>
                            Drag & drop files here OR click to upload (Max 10 files)
                        </Typography>

                        <input
                            type="file"
                            multiple
                            onChange={handleFileChange}
                            style={{ marginTop: "10px" }}
                            disabled={files.length >= 10}
                        />
                    </div>
                </div>
                {files.length > 0 && (
                    <div style={{ marginTop: "10px" }}>
                        {files.map((file, index) => (
                            <div
                                key={index}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    border: "1px solid #ddd",
                                    borderRadius: "6px",
                                    padding: "6px 10px",
                                    marginBottom: "6px"
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>

                                    {file.type.startsWith("image/") && (
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt="preview"
                                            style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
                                        />
                                    )}

                                    <div>
                                        <div style={{ fontSize: "13px" }}>{file.name}</div>
                                        <div style={{ fontSize: "11px", color: "gray" }}>
                                            {(file.size / 1024).toFixed(1)} KB
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteFile(index)}
                                >
                                    Delete
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
                <div className="btn-container">
                    <Button
                        type="submit"
                        className="mailer-generate-btn filled-btn next-btn"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={18} color="inherit" style={{ marginRight: "8px" }} />
                                Submitting...
                            </>
                        ) : (
                            "Submit"
                        )}
                    </Button>
                </div>

                <Snackbar
                    open={toast.open}
                    autoHideDuration={3000}
                    onClose={() => setToast({ ...toast, open: false })}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert
                        onClose={() => setToast({ ...toast, open: false })}
                        severity={toast.severity}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {toast.message}
                    </Alert>
                </Snackbar>
            </Stack>
        </div>
    );
};

export default CampaignForm;